import {
	CheckCircle,
	ChevronDown,
	ChevronUp,
	SlashCircle01,
	Tool02,
	Trash01,
} from "@untitledui/icons";
import { useState } from "react";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Toggle } from "@/components/base/toggle/toggle";
import { OptionDisplayField } from "@/features/questions/components";
import {
	QuestionType as QType,
	QuestionOptionDisplayType,
	type Question as QuestionType,
} from "@/types/question";

const CHECKLIST_ICON_MAP: Record<
	string,
	React.ComponentType<{ className?: string }>
> = {
	Regular: CheckCircle,
	Avariado: Tool02,
	"Não possui": SlashCircle01,
};

export type ActiveQuestionComponentProps = {
	question: QuestionType;
	onDelete?: (questionId: number) => void;
	onRequiredChange?: (questionId: number, required: boolean) => void;
};

const mapBackendTypeToEnum: Record<number, QType> = {
	1: QType.Option,
	2: QType.Date,
	3: QType.Time,
	5: QType.Text,
	6: QType.Number,
	7: QType.File,
	8: QType.Boolean,
};

export const ActiveQuestionComponent = ({
	question,
	onDelete,
	onRequiredChange,
}: ActiveQuestionComponentProps) => {
	const [isRequired, setIsRequired] = useState(question.isRequired ?? false);
	const [isCollapsed, setIsCollapsed] = useState(false);

	const handleRequiredChange = (checked: boolean) => {
		setIsRequired(checked);
		onRequiredChange?.(question.id, checked);
	};

	const toggleCollapse = () => setIsCollapsed((prev) => !prev);

	const normalizedType =
		mapBackendTypeToEnum[
			(question as QuestionType & { questionType?: number }).questionType ??
				question.type
		] ?? question.type;

	const renderOptionPreview = () => {
		const settingsWithOptions = question.settings as
			| (NonNullable<QuestionType["settings"]> & {
					options?: { id: number; label: string; position?: number }[];
			  })
			| undefined;
		const options =
			settingsWithOptions?.options ||
			settingsWithOptions?.questionOptions ||
			[];
		const displayType =
			question.settings?.optionDisplayType ??
			QuestionOptionDisplayType.Checkbox;

		if (!options.length) {
			return <p className="text-gray-500 text-sm">Nenhuma opção configurada</p>;
		}

		const sortedOptions = [...options].sort(
			(a, b) => (a.position || 0) - (b.position || 0)
		);

		if (displayType === QuestionOptionDisplayType.Icon) {
			return (
				<div className="space-y-2">
					<h3 className="font-medium text-gray-700 text-sm">
						Opções de resposta
					</h3>
					<div className="grid grid-cols-3 gap-3">
						{sortedOptions.map((option) => {
							const IconComponent = CHECKLIST_ICON_MAP[option.label];
							return (
								<div
									className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4"
									key={option.id}
								>
									{IconComponent ? (
										<IconComponent className="mb-2 h-6 w-6 text-gray-600" />
									) : null}
									<span className="text-center font-medium text-gray-700 text-sm">
										{option.label}
									</span>
								</div>
							);
						})}
					</div>
				</div>
			);
		}

		const optionFieldValue = question.settings?.isMultipleSelection ? [] : "";

		return (
			<div className="space-y-2">
				<h3 className="font-medium text-gray-700 text-sm">
					Opções de resposta
				</h3>
				<OptionDisplayField
					ariaLabel={question.prompt}
					isDisabled
					isMultipleSelection={Boolean(question.settings?.isMultipleSelection)}
					optionDisplayType={displayType}
					options={sortedOptions}
					value={optionFieldValue}
				/>
			</div>
		);
	};

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: preview switch covers all question types
	const renderQuestionPreview = () => {
		switch (normalizedType) {
			case QType.Text:
				return (
					<Input
						isDisabled
						placeholder={
							question.settings?.maxLength
								? `Máx. ${question.settings.maxLength} caracteres`
								: "Digite sua resposta"
						}
					/>
				);
			case QType.Number:
				return (
					<Input
						isDisabled
						placeholder={
							question.settings?.minValue !== undefined ||
							question.settings?.maxValue !== undefined
								? `Entre ${question.settings.minValue ?? "..."} e ${question.settings.maxValue ?? "..."}`
								: "Digite um número"
						}
						type="number"
					/>
				);
			case QType.Date:
				return (
					<>
						<Input isDisabled type="date" />
						{question.settings?.dateRangeType !== undefined && (
							<p className="text-gray-500 text-xs">
								{{
									0: "Qualquer data permitida",
									1: "Apenas datas passadas",
									2: "Apenas datas futuras",
									3: "Data dentro do intervalo",
								}[question.settings.dateRangeType] ?? "Intervalo de datas"}
							</p>
						)}
					</>
				);
			case QType.Time:
				return <Input isDisabled type="time" />;
			case QType.File:
				return (
					<div className="rounded-lg border-2 border-gray-300 border-dashed bg-gray-50 p-6 text-center text-gray-500 text-sm">
						Tamanho Máximo: {question.settings?.maxFileSizeMB ?? 10} MB por
						foto.
					</div>
				);
			case QType.Boolean:
				return (
					<Checkbox
						isDisabled
						isSelected={false}
						label={
							<span className="inline-flex items-start gap-1 text-gray-700 text-sm">
								<span>{question.prompt}</span>
								{isRequired ? (
									<span className="shrink-0 text-brand-tertiary">*</span>
								) : null}
							</span>
						}
					/>
				);
			case QType.Option:
				return renderOptionPreview();
			default:
				return <Input isDisabled placeholder="Campo de resposta" />;
		}
	};

	return (
		<div className="relative rounded-2xl border border-gray-300 bg-white p-6 shadow-sm">
			<button
				className="-top-2 -left-2 absolute flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 shadow-sm transition hover:bg-gray-100 hover:text-gray-700"
				onClick={toggleCollapse}
				title={isCollapsed ? "Expandir pergunta" : "Minimizar pergunta"}
				type="button"
			>
				{isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
			</button>

			<div className="flex items-start gap-4">
				<div className="grow space-y-4 overflow-x-auto">
					{normalizedType === QType.Boolean && question.description ? (
						<p className="wrap-break-word text-gray-500 text-sm">
							{question.description}
						</p>
					) : null}
					{normalizedType !== QType.Boolean ? (
						<div className="flex flex-col gap-1">
							<h1 className="font-semibold text-gray-900 text-lg">
								{question.prompt}
							</h1>
							{question.description ? (
								<p className="wrap-break-word text-gray-500 text-sm">
									{question.description}
								</p>
							) : null}
						</div>
					) : null}

					{!isCollapsed && (
						<>
							{renderQuestionPreview()}
							<div className="flex items-center justify-end gap-3 pt-2">
								<Toggle
									isSelected={isRequired}
									onChange={handleRequiredChange}
								/>
								<span className="font-medium text-gray-700 text-sm">
									Obrigatória
								</span>
							</div>
						</>
					)}
				</div>

				<button
					className="rounded-lg border border-gray-200 p-2"
					onClick={() => onDelete?.(question.id)}
					title="Remover"
					type="button"
				>
					<Trash01 color="#A4A7AE" size={20} />
				</button>
			</div>
		</div>
	);
};
